'use client'

import Image from 'next/image'
import {sanityImageLoader} from '@/sanity/imageLoader'

/**
 * The layout-group background image.
 *
 * This exists for one reason: **`loader` is a function, and a function cannot
 * be passed from a server component to a client component.** `next/image` is a
 * client component, so `<Image loader={sanityImageLoader} />` written inline in
 * a server component throws at render:
 *
 *     Error: Functions cannot be passed directly to Client Components unless
 *     you explicitly expose it by marking it with "use server".
 *
 * That shipped in JO2-139 and took down every site front page on develop for
 * six days (#961) — every front page whose layout group had a background image,
 * which on the `development` dataset was all of them. Production was spared
 * only because the commit had not been released yet.
 *
 * Moving the `<Image>` behind `'use client'` means the loader is imported on
 * the client side of the boundary and never crosses it. The URL is still built
 * on the server, so `urlForImage` and the Sanity client stay server-only.
 *
 * `sizes="100vw"` deliberately overestimates: this block also renders at
 * contained widths, and overestimating picks a larger candidate rather than a
 * blurry one. No `priority` — being lazy is half the point of JO2-139, which
 * replaced a CSS `background-image` precisely so these could carry a srcset and
 * load lazily (two on the NO homepage were 5.4 MB, 81% of the page, offscreen).
 */
export const LayoutBackgroundImage = ({
  src,
  objectPosition,
}: {
  src: string
  /** From `objectPositionFromHotspot()`. Without it a phone crops to centre (JO2-245). */
  objectPosition?: string
}) => (
  <Image
    src={src}
    alt=""
    aria-hidden
    fill
    sizes="100vw"
    loader={sanityImageLoader}
    className="object-cover"
    style={objectPosition ? {objectPosition} : undefined}
  />
)
